import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowRight, MessageCircle } from "lucide-react";
import { BRAND } from "@/lib/brand";
import useSeo from "@/lib/useSeo";

export default function ContactPage() {
  useSeo({
    title: "Contact — Top-Notch Pro Solutions | Handyman & Cleaning Evansville, IN",
    description: "Call (812) 708-0007 or email Top-Notch Pro Solutions for premium handyman and residential cleaning services in Evansville, IN. Mon–Sat 8 AM–6 PM. Serving a 20-mile radius.",
  });
  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24">
      <div className="max-w-3xl">
        <span className="section-eyebrow">Get In Touch</span>
        <h1 className="heading-display mt-3 text-4xl md:text-6xl">We're Right Around the Corner.</h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          Have a question about a service? Curious if your address falls inside our 20-mile radius? We'd love to chat.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[
          { icon: Phone,  title: "Call Us",   value: BRAND.phone, href: BRAND.phoneHref, sub: "Fastest response — direct line." },
          { icon: Mail,   title: "Email",     value: BRAND.email, href: `mailto:${BRAND.email}`, sub: "We reply within 24 hours, Mon–Sat." },
          { icon: MapPin, title: "Visit / Mail", value: BRAND.address, sub: "By appointment only." },
        ].map((c) => (
          <div key={c.title} className="card-elevated p-7" data-testid={`contact-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="w-12 h-12 rounded-xl bg-[var(--brand-teal)]/10 text-[var(--brand-teal)] flex items-center justify-center mb-5">
              <c.icon className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-[var(--brand-teal)] text-lg mb-2" style={{ fontFamily: "Outfit" }}>{c.title}</h3>
            {c.href ? (
              <a href={c.href} className="text-slate-700 font-semibold hover:text-[var(--brand-gold-deep)] block break-words">{c.value}</a>
            ) : (
              <p className="text-slate-700 font-semibold break-words">{c.value}</p>
            )}
            <p className="text-sm text-slate-500 mt-2">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-elevated p-7">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-6 h-6 text-[var(--brand-gold-deep)]" />
            <h3 className="font-bold text-[var(--brand-teal)] text-lg" style={{ fontFamily: "Outfit" }}>Hours of Operation</h3>
          </div>
          <ul className="space-y-2 text-slate-700">
            {BRAND.hours.map((h) => (
              <li key={h.day} className="flex justify-between border-b border-dashed border-[#dcd5c6] pb-2">
                <span className="font-semibold">{h.day}</span>
                <span>{h.time}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-elevated p-7 bg-[var(--brand-teal)] text-white border-[var(--brand-teal)]">
          <div className="flex items-center gap-3 mb-4">
            <MessageCircle className="w-6 h-6 text-[var(--brand-gold)]" />
            <h3 className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>Skip the Phone Tag</h3>
          </div>
          <p className="text-white/85 leading-relaxed">
            Our online quote builder gives you a real, instant price for cleaning services using transparent matrices —
            no waiting on a callback. Lock your slot in under 60 seconds.
          </p>
          <Link to="/quote" className="btn-accent mt-6" data-testid="contact-cta-quote">
            Get Instant Booking <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
